import { useMemo, useState, useCallback, useEffect } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  TextField,
  Alert,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Chip
} from '@mui/material';
import { ExpandMore as ExpandIcon } from '@mui/icons-material';
import { useEngagementEditor } from './EngagementEditorContext';
import { getProductType } from '../../../../constants/advise/productTypes';
import { formatCurrency } from '../../../../utils/helpers';
import { calcSupplierRoi, SupplierRoiResult } from './RoiAnalysisPanel.calc';

interface RoiState {
  currentSpend: Record<string, number>;
  metrics: Record<string, number>;
  supplierProposals: Record<string, Record<string, number>>;
}

const CURRENT_SPEND_FIELDS = [
  { key: 'monthly_platform', label: 'Monthly Platform Cost' },
  { key: 'monthly_telco', label: 'Monthly Telco Cost' },
  { key: 'monthly_wfm', label: 'Monthly WFM Cost' },
  { key: 'monthly_qm', label: 'Monthly QM Cost' },
  { key: 'monthly_recording', label: 'Monthly Recording Cost' },
  { key: 'monthly_other', label: 'Monthly Other Cost' }
];

const METRICS_FIELDS = [
  { key: 'num_agents', label: 'Number of Agents' },
  { key: 'agent_salary', label: 'Average Agent Salary ($)' },
  {
    key: 'annual_inbound_calls',
    label: 'Annual Inbound Calls'
  },
  {
    key: 'annual_outbound_calls',
    label: 'Annual Outbound Calls'
  },
  {
    key: 'avg_handle_time',
    label: 'Average Handle Time (min)'
  },
  {
    key: 'conversion_rate',
    label: 'Current Conversion Rate (%)'
  },
  {
    key: 'revenue_per_opportunity',
    label: 'Revenue per Opportunity ($)'
  },
  { key: 'it_fte', label: 'IT FTEs Supporting CC' },
  { key: 'qa_fte', label: 'QA FTEs' },
  { key: 'wfm_fte', label: 'WFM FTEs' }
];

const SUPPLIER_PROPOSAL_FIELDS = [
  {
    key: 'proposed_monthly_cost',
    label: 'Proposed Monthly Cost'
  },
  { key: 'implementation_cost', label: 'Implementation Cost' },
  {
    key: 'aht_reduction_pct',
    label: 'AHT Reduction (%)'
  },
  {
    key: 'deflection_rate_pct',
    label: 'Call Deflection (%)'
  },
  {
    key: 'conversion_improvement_pct',
    label: 'Conversion Improvement (%)'
  },
  {
    key: 'fte_reduction_pct',
    label: 'FTE Reduction (%)'
  },
  {
    key: 'attrition_reduction_pct',
    label: 'Attrition Reduction (%)'
  }
];

const formatPct = (pct: number) => {
  if (!pct || isNaN(pct)) return '0%';
  return `${Math.round(pct)}%`;
};

export const RoiAnalysisPanel = () => {
  const { engagement, suppliers, canEdit, editEngagement } = useEngagementEditor();
  const selectedSuppliers = useMemo(
    () => suppliers.filter((v) => engagement.selected_suppliers?.includes(v.id)),
    [suppliers, engagement.selected_suppliers]
  );

  const productType = useMemo(() => getProductType(engagement.product_type), [engagement.product_type]);

  const roiData = (engagement.roi_analysis || {}) as unknown as RoiState;

  const [localState, setLocalState] = useState<RoiState>({
    currentSpend: roiData.currentSpend || {},
    metrics: roiData.metrics || {},
    supplierProposals: roiData.supplierProposals || {}
  });

  // Re-init only when a different engagement loads — avoids clobbering in-progress edits
  // when our own debounced save round-trips engagement.roi_analysis.
  useEffect(() => {
    setLocalState({
      currentSpend: roiData.currentSpend || {},
      metrics: roiData.metrics || {},
      supplierProposals: roiData.supplierProposals || {}
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [engagement.id]);

  // TODO(UPDEV-8042): clamp ROI numeric inputs — reject negatives and cap *_pct fields at 100 before they feed supplier totals.
  const handleFieldChange = useCallback((section: 'currentSpend' | 'metrics', key: string, value: string) => {
    setLocalState((prev) => ({
      ...prev,
      [section]: {
        ...prev[section],
        [key]: parseFloat(value) || 0
      }
    }));
  }, []);

  const handleSupplierFieldChange = useCallback((supplierId: string, key: string, value: string) => {
    setLocalState((prev) => ({
      ...prev,
      supplierProposals: {
        ...prev.supplierProposals,
        [supplierId]: {
          ...(prev.supplierProposals[supplierId] || {}),
          [key]: parseFloat(value) || 0
        }
      }
    }));
  }, []);

  const handleBlur = useCallback(() => {
    editEngagement({ roi_analysis: localState as unknown as Record<string, unknown> });
  }, [localState, editEngagement]);

  const supplierRoi = useMemo(() => {
    const results: Record<string, SupplierRoiResult> = {};
    selectedSuppliers.forEach((v) => {
      results[v.id] = calcSupplierRoi(
        localState.currentSpend,
        localState.metrics,
        localState.supplierProposals[v.id] || {}
      );
    });
    return results;
  }, [localState, selectedSuppliers]);

  if (!productType) {
    return <Alert severity="info">No product type configured.</Alert>;
  }

  if (selectedSuppliers.length === 0) {
    return <Alert severity="info">No suppliers selected for ROI analysis.</Alert>;
  }

  return (
    <Box className="flex flex-col gap-4">
      {/* ROI Summary Cards */}
      <Box className="grid grid-cols-1 md:grid-cols-3 gap-3">
        {selectedSuppliers.map((v) => {
          const roi = supplierRoi[v.id];
          if (!roi) return null;
          return (
            <Card
              key={v.id}
              variant="outlined">
              <CardContent>
                <Typography
                  variant="subtitle2"
                  gutterBottom>
                  {v.supplier_name}
                </Typography>
                <Box className="flex flex-col gap-1">
                  <Box className={'flex justify-between items-center'}>
                    <Typography
                      variant="caption"
                      color="textSecondary">
                      Annual Savings
                    </Typography>
                    <Typography
                      variant="body2"
                      color={roi.annualSavings > 0 ? 'success.main' : 'error.main'}>
                      {formatCurrency(roi.annualSavings, { decimals: 0 })}
                    </Typography>
                  </Box>
                  <Box className={'flex justify-between items-center'}>
                    <Typography
                      variant="caption"
                      color="textSecondary">
                      Added Revenue
                    </Typography>
                    <Typography variant="body2">{formatCurrency(roi.addedRevenue, { decimals: 0 })}</Typography>
                  </Box>
                  <Box className={'flex justify-between items-center'}>
                    <Typography
                      variant="caption"
                      color="textSecondary">
                      Net Year 1
                    </Typography>
                    <Typography
                      variant="subtitle2"
                      color={roi.netYear1 > 0 ? 'success.main' : 'error.main'}>
                      {formatCurrency(roi.netYear1, { decimals: 0 })}
                    </Typography>
                  </Box>
                  <Box className="flex justify-end mt-1">
                    <Chip
                      label={`${formatPct(roi.roiPct)} ROI`}
                      size="small"
                      color={roi.roiPct > 0 ? 'success' : 'error'}
                    />
                  </Box>
                </Box>
              </CardContent>
            </Card>
          );
        })}
      </Box>

      {/* Current State */}
      <Accordion defaultExpanded>
        <AccordionSummary expandIcon={<ExpandIcon />}>
          <Typography variant="subtitle2">Current State — Monthly Spend</Typography>
        </AccordionSummary>
        <AccordionDetails>
          <Box className="grid grid-cols-2 md:grid-cols-3 gap-3">
            {CURRENT_SPEND_FIELDS.map((field) => (
              <TextField
                key={field.key}
                label={field.label}
                type="number"
                size="small"
                value={localState.currentSpend[field.key] || ''}
                onChange={(e) => handleFieldChange('currentSpend', field.key, e.target.value)}
                onBlur={handleBlur}
                disabled={!canEdit}
                fullWidth
              />
            ))}
          </Box>
        </AccordionDetails>
      </Accordion>

      {/* Agent & Team Metrics */}
      <Accordion defaultExpanded>
        <AccordionSummary expandIcon={<ExpandIcon />}>
          <Typography variant="subtitle2">Agent & Team Metrics</Typography>
        </AccordionSummary>
        <AccordionDetails>
          <Box className="grid grid-cols-2 md:grid-cols-3 gap-3">
            {METRICS_FIELDS.map((field) => (
              <TextField
                key={field.key}
                label={field.label}
                type="number"
                size="small"
                value={localState.metrics[field.key] || ''}
                onChange={(e) => handleFieldChange('metrics', field.key, e.target.value)}
                onBlur={handleBlur}
                disabled={!canEdit}
                fullWidth
              />
            ))}
          </Box>
        </AccordionDetails>
      </Accordion>

      {/* Supplier Proposals */}
      {selectedSuppliers.map((supplier) => (
        <Accordion key={supplier.id}>
          <AccordionSummary expandIcon={<ExpandIcon />}>
            <Typography variant="subtitle2">{supplier.supplier_name} — Proposal Inputs</Typography>
          </AccordionSummary>
          <AccordionDetails>
            <Box className={'grid grid-cols-2 md:grid-cols-3 gap-3'}>
              {SUPPLIER_PROPOSAL_FIELDS.map((field) => (
                <TextField
                  key={field.key}
                  label={field.label}
                  type="number"
                  size="small"
                  value={localState.supplierProposals[supplier.id]?.[field.key] || ''}
                  onChange={(e) => handleSupplierFieldChange(supplier.id, field.key, e.target.value)}
                  onBlur={handleBlur}
                  disabled={!canEdit}
                  fullWidth
                />
              ))}
            </Box>
          </AccordionDetails>
        </Accordion>
      ))}
    </Box>
  );
};
